/**
 * Copilot Sidebar — conversational surface
 * ═══════════════════════════════════════════════════════════════
 * Phase 2 · task 2.15 (surface 2 of 5)
 *
 * The panel for anything longer than a one-line answer. It carries the
 * resolved copilot's identity, its answers, and any trigger rule that fired
 * while the user was working.
 *
 * Rendered only when the resolved copilot declares `surfaces.sidebar`.
 */
import { useState, useRef, useEffect } from 'react';
import { Sparkles, X, Send, Circle, ShieldOff, Loader2 } from 'lucide-react';

export default function CopilotSidebar({
  agent,
  open,
  onClose,
  onAsk,
  messages = [],
  busy = false,
  recording = false,
  firedTriggers = [],
  onDismissTrigger,
  onOpenPrivacy,
}) {
  const [input, setInput] = useState('');
  const endRef = useRef(null);

  useEffect(() => {
    // Copy before any sort/mutation — RTK Query arrays are Immer-frozen.
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, busy]);

  if (!open || !agent) return null;

  const submit = () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    onAsk?.(text);
  };

  return (
    <aside
      className="fixed top-0 right-0 h-full w-[380px] max-w-[92vw] z-[9992]
                 bg-white border-l border-gray-200 shadow-2xl flex flex-col"
      role="complementary"
      aria-label={`${agent.name} panel`}
    >
      <header className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100 shrink-0">
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600
                        flex items-center justify-center text-white shrink-0">
          <Sparkles size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-gray-800 truncate">{agent.name}</p>
          {agent.matchedPattern && (
            <p className="text-[11px] text-gray-400 truncate">bound to {agent.matchedPattern}</p>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0" aria-label="Close panel">
          <X size={16} />
        </button>
      </header>

      {/* Task 2.17 — visible indicator, with a direct route to turning it off.
          An indicator that only informs is worse than one that also acts. */}
      {recording && (
        <div className="flex items-center gap-1.5 px-4 py-2 bg-amber-50 border-b border-amber-100 shrink-0">
          <Circle size={7} className="text-amber-500 fill-amber-500 shrink-0" />
          <span className="text-[11px] text-amber-700 flex-1">Recording your actions on this page</span>
          <button
            onClick={onOpenPrivacy}
            className="text-[11px] text-amber-800 underline underline-offset-2 hover:text-amber-900 shrink-0"
          >
            Turn off
          </button>
        </div>
      )}

      {/* Task 2.16 — a fired trigger appears here rather than interrupting. */}
      {firedTriggers.length > 0 && (
        <div className="px-3 py-2 space-y-1.5 border-b border-gray-100 shrink-0">
          {firedTriggers.map((t) => (
            <div key={t.id} className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
              <Sparkles size={12} className="text-blue-500 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-blue-900">{t.label}</p>
                {t.prompt && <p className="text-[11px] text-blue-800/80 mt-0.5">{t.prompt}</p>}
              </div>
              <button
                onClick={() => onDismissTrigger?.(t.id)}
                className="text-blue-400 hover:text-blue-600 shrink-0"
                aria-label="Dismiss suggestion"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && !busy && (
          <div className="text-center py-10">
            <Sparkles size={20} className="text-gray-300 mx-auto mb-2" />
            <p className="text-[12px] text-gray-400">Ask {agent.name} about this page.</p>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div
              className={`max-w-[86%] rounded-2xl px-3 py-2 text-[12px] whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-blue-500 text-white rounded-br-sm'
                  : 'bg-gray-50 text-gray-800 rounded-bl-sm border border-gray-100'
              }`}
            >
              {m.content}
              {/* A refusal is shown as a refusal, not folded into prose. */}
              {m.blocked && (
                <span className="mt-1.5 flex items-center gap-1 text-[11px] opacity-80">
                  <ShieldOff size={10} /> blocked by this copilot&apos;s guardrails
                </span>
              )}
            </div>
          </div>
        ))}

        {busy && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-gray-50 border border-gray-100 px-3 py-2">
              <Loader2 size={13} className="text-gray-400 animate-spin" />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t border-gray-100 p-2.5 shrink-0">
        <div className="flex items-end gap-1.5">
          <textarea
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
            }}
            placeholder={`Ask ${agent.name}…`}
            className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-[12px]
                       outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 max-h-28"
          />
          <button
            onClick={submit}
            disabled={busy || !input.trim()}
            className="h-8 w-8 rounded-xl bg-blue-500 text-white flex items-center justify-center
                       disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-600 shrink-0"
            aria-label="Send"
          >
            <Send size={13} />
          </button>
        </div>
      </div>
    </aside>
  );
}
